import { useState, useEffect } from "react";
import { Eye, EyeOff, Plus } from "lucide-react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";

export default function AdminProfile() {
  const [admins, setAdmins] = useState([]);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const adminsCollection = collection(db, "admins");

  const fetchAdmins = async () => {
    const data = await getDocs(adminsCollection);
    setAdmins(data.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const resetForm = () => {
    setUsername("");
    setPassword("");
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;

    if (editingId) {
      await updateDoc(doc(db, "admins", editingId), { username, password });
    } else {
      await addDoc(adminsCollection, { username, password });
    }

    resetForm();
    fetchAdmins();
  };

  const handleEdit = (admin) => {
    setUsername(admin.username);
    setPassword(admin.password);
    setEditingId(admin.id);
    setShowForm(true);
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    if (admins.length === 1) {
      alert("At least one admin must exist!");
      setShowDeleteModal(false);
      return;
    }
    await deleteDoc(doc(db, "admins", deleteId));
    setShowDeleteModal(false);
    fetchAdmins();
  };

  return (
    <div className="min-h-screen bg-[var(--bg-1)] p-4 sm:p-6 md:p-10">
      {/* ================= HEADER ================= */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl text-[var(--brand-dark)]">
  Admin Management
</h1>

          <p className="text-sm text-gray-500">
            Manage admin access and credentials
          </p>
        </div>

        <button
          onClick={() => setShowForm(true)}
          className="bg-[var(--brand-gold)] text-white px-5 py-3 rounded-lg font-medium shadow hover:shadow-lg transition flex items-center gap-2"
        >
          <Plus size={18} /> Add Admin
        </button>
      </div>

      {/* ================= FORM ================= */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-md p-5 sm:p-6 md:p-8 mb-10 border border-[var(--bg-3)] w-full max-w-2xl overflow-hidden">
          <h2 className="text-xl font-semibold text-[var(--brand-dark)] mb-6">
            {editingId ? "Edit Admin" : "Add New Admin"}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {/* Username */}
            <div>
              <label className="text-sm font-medium text-[var(--brand-dark)]">
                Username <span className="text-red-500">*</span>
              </label>
              <input
                className="mt-1 w-full border rounded-md
                px-3 sm:px-4
                h-10 sm:h-[42px]
                focus:outline-none focus:border-[var(--brand-gold)]"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
              />
            </div>

            {/* Password */}
            <div>
              <label className="text-sm font-medium text-[var(--brand-dark)]">
                Password <span className="text-red-500">*</span>
              </label>

              <div
                className="mt-1 flex items-center border rounded-md
                px-3 sm:px-4
                h-10 sm:h-[42px]
                focus-within:ring-2 focus-within:ring-[var(--brand-gold)]"
              >
                <input
                  type={showPassword ? "text" : "password"}
                  className="flex-1 bg-transparent border-none focus:outline-none h-full"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                />

                <button
                  type="button"
                  className="ml-2 text-gray-600 hover:text-gray-800 flex items-center justify-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSubmit}
              className="flex-1 bg-[var(--brand-dark)] text-white px-6 py-2 rounded-md hover:opacity-90 transition"
            >
              {editingId ? "Update Admin" : "Save Admin"}
            </button>
            <button
              onClick={resetForm}
              className="flex-1 bg-gray-300 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-400 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ================= TABLE ================= */}
      <div className="bg-white rounded-xl shadow-md p-5 md:p-8 border border-[var(--bg-3)]">
        <h2 className="text-xl mb-6">
  All Admins
</h2>


        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[400px]">
            <thead>
              <tr className="bg-[var(--bg-3)] text-[var(--brand-dark)]">
                <th className="text-left p-3">Username</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr
                  key={admin.id}
                  className="border-b hover:bg-[var(--bg-2)]"
                >
                  <td className="p-3">{admin.username}</td>
                  <td className="p-3 text-right space-x-3">
                    <button
                      onClick={() => handleEdit(admin)}
                      className="text-[var(--brand-gold)] hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => confirmDelete(admin.id)}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {admins.length === 0 && (
                <tr>
                  <td
                    colSpan="2"
                    className="p-6 text-center text-gray-500"
                  >
                    No admins added yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= DELETE MODAL ================= */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-lg">
            <h3 className="text-lg font-semibold mb-3">
              Confirm Delete
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this admin?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}